/**
 * useStoreDetail — 매장 상세 정보 조회 훅 (#632)
 *
 * GET /api/v1/stores/:storeParam 응답(StoreDetail: address/phone 등)을 제공.
 * 영수증(RestaurantInfo) 등 주문 응답에 매장 주소가 없는 화면에서
 * store_id 기반으로 실제 매장 정보를 보충 조회할 때 사용.
 *
 * 실패 시 store=null 유지 (fail-soft) — 소비처는 주소 줄 숨김으로 처리하고
 * 더미 데이터 폴백은 금지 (#632 더미 주소 'Calle Table Order 123' 시정).
 */
import { useState, useEffect, useRef } from 'react';
import { storeApi, type StoreDetail } from '@/lib/api';
import { logger } from '@/utils/logger';

interface UseStoreDetailState {
  store: StoreDetail | null;
  loading: boolean;
  error: Error | null;
}

/**
 * 매장 상세 조회 훅
 * @param storeParam 매장 슬러그 또는 ID — undefined/빈 문자열이면 조회하지 않음
 */
export function useStoreDetail(storeParam: string | undefined): UseStoreDetailState {
  const [state, setState] = useState<UseStoreDetailState>({
    store: null,
    loading: false,
    error: null,
  });

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const trimmed = storeParam?.trim();
    if (!trimmed) {
      setState({ store: null, loading: false, error: null });
      return;
    }

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    // I1 (#632 codex): storeParam 변경 시 이전 매장 store 를 즉시 비움 —
    // prev.store 유지 시 새 매장 응답 도착 전까지 이전 매장 주소가 영수증에
    // 일시 표시/인쇄될 수 있음 (stale 표시 금지, 줄 숨김이 정답).
    setState({ store: null, loading: true, error: null });

    (async () => {
      try {
        const response = await storeApi.getStore(trimmed, controller.signal);
        if (controller.signal.aborted) return;

        // 응답 가드: data 부재 시 store=null 유지 (silent dummy fallback 금지)
        setState({ store: response?.data ?? null, loading: false, error: null });
      } catch (err) {
        if (controller.signal.aborted) return;
        // fail-soft: 매장 정보는 보조 표시 데이터 — 에러는 기록만 하고 store=null
        logger.warn('[useStoreDetail] 매장 상세 조회 실패:', err);
        setState({
          store: null,
          loading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [storeParam]);

  return state;
}
